import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Download, Building2, ArrowLeftRight, Landmark, Wallet, ChevronDown, ChevronRight } from 'lucide-react';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const fmt = v => (v != null && v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) !== 0)
  ? `₹${parseFloat(v).toLocaleString('en-IN')}` : '—';

const toRoman = n => {
  const vals = [1000,900,500,400,100,90,50,40,10,9,5,4,1];
  const syms = ['m','cm','d','cd','c','xc','l','xl','x','ix','v','iv','i'];
  let r = ''; vals.forEach((v,i) => { while(n>=v){r+=syms[i];n-=v;} }); return r;
};

const getItemType = it => {
  if (it.payment_type === 'vendor') return { label: 'Invoice', bg: '#fef3c7', color: '#92400e' };
  if (it.section === 'invoices')    return { label: 'Claim',   bg: '#ede9fe', color: '#5b21b6' };
  if (it.isUnplanned)               return { label: 'Misc.',   bg: '#fff7ed', color: '#c2410c' };
  return                                   { label: 'Expense', bg: '#f0fdf4', color: '#065f46' };
};

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function computeGroups(items) {
  const office   = (items || []).filter(it => it.section !== 'invoices' && it.payment_type !== 'vendor' && it.description);
  const internal = (items || []).filter(it => it.section === 'invoices' && it.payment_type !== 'vendor' && it.description);
  const vendor   = (items || []).filter(it => it.payment_type === 'vendor' && it.description);
  const officeT   = office.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
  const internalT = internal.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
  const vendorT   = vendor.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
  return { office, internal, vendor, officeT, internalT, vendorT, grandTotal: officeT + internalT + vendorT };
}

const GROUP_STYLES = {
  office:   { border: '#10b981', bg: '#f0fdf4', text: '#065f46', icon: Building2,      label: 'Office Expense' },
  internal: { border: '#3b82f6', bg: '#eff6ff', text: '#1e40af', icon: ArrowLeftRight, label: 'Internal Transfer Invoices' },
  vendor:   { border: '#f59e0b', bg: '#fffbeb', text: '#78350f', icon: Landmark,       label: 'Direct Vendor Payments' },
};

function GroupCard({ groupKey, items, total, isOpen, onToggle }) {
  const s = GROUP_STYLES[groupKey];
  const Icon = s.icon;
  if (!total && !items.length) return null;
  return (
    <div style={{ borderBottom: '1px solid #f1f5f9' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 20px', border: 'none', background: isOpen ? s.bg : 'transparent',
          borderLeft: `4px solid ${s.border}`, cursor: 'pointer', textAlign: 'left',
        }}
        onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center',
            justifyContent: 'center', background: s.bg, flexShrink: 0,
          }}>
            <Icon size={14} color={s.border} />
          </span>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: s.text }}>{s.label}</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: s.text, fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</span>
          {isOpen ? <ChevronDown size={14} color={s.text} /> : <ChevronRight size={14} color="#94a3b8" />}
        </span>
      </button>
      {isOpen && (
        <div style={{ background: '#f8fafc', padding: '4px 0' }}>
          {items.map((it, i) => (
            <div key={it.id || i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '7px 20px 7px 52px', fontSize: 13, borderBottom: '1px solid #f1f5f9',
            }}>
              <span style={{ color: '#475569' }}>
                {it.description}
                {it.additional_comment && <span style={{ color: '#94a3b8', marginLeft: 8 }}>({it.additional_comment})</span>}
              </span>
              <span style={{ color: '#1e293b', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                {fmt(it.approximate_amount) !== '—' ? fmt(it.approximate_amount) : fmt(it.actual_amount)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ padding: '10px 20px', borderBottom: '1px solid #e2e8f0' }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</span>
    </div>
  );
}

function ClassRow({ label, value, borderColor, textColor, bg }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '11px 20px', borderLeft: `4px solid ${borderColor}`,
      background: bg, borderBottom: '1px solid #f1f5f9',
    }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: textColor }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: textColor, fontVariantNumeric: 'tabular-nums' }}>{fmt(value)}</span>
    </div>
  );
}

function RecRow({ label, value, bold, color, bg, plus, minus }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 20px', background: bg || '#fff', borderBottom: '1px solid #f1f5f9',
    }}>
      <span style={{ fontSize: 13, color: color || '#334155', fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: color || '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
        {plus && '+'} {minus && '−'} {fmt(value)}
      </span>
    </div>
  );
}

function CommentsSection({ token }) {
  const [comments, setComments] = useState([]);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');

  const load = () =>
    axios.get(`/api/expenses/public/${token}/comments/`)
      .then(r => setComments(r.data))
      .catch(() => {});

  useEffect(() => { load(); }, [token]);

  const submit = async () => {
    if (!name.trim() || !text.trim()) { setToast('Please enter your name and comment.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`/api/expenses/public/${token}/comments/`, { author_name: name.trim(), text: text.trim() });
      setText('');
      setToast('Comment posted!');
      load();
      setTimeout(() => setToast(''), 3000);
    } catch { setToast('Failed to post. Please try again.'); }
    setSubmitting(false);
  };

  const fmtTime = d => new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <SectionLabel>Comments &amp; Feedback</SectionLabel>
        <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {comments.length === 0 && (
            <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>No comments yet. Be the first to leave a note.</p>
          )}
          {comments.map(c => (
            <div key={c.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#10b981' }}>{c.author_name}</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtTime(c.created_at)}</span>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{c.text}</p>
            </div>
          ))}

          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="Your name"
              style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
            />
            <textarea
              value={text} onChange={e => setText(e.target.value)}
              placeholder="Leave a comment or note for the team…"
              rows={3}
              style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, resize: 'vertical', fontFamily: 'inherit', outline: 'none' }}
            />
            {toast && <div style={{ fontSize: 12, color: toast.startsWith('Comment') ? '#10b981' : '#ef4444' }}>{toast}</div>}
            <div style={{ textAlign: 'right' }}>
              <button
                onClick={submit} disabled={submitting}
                style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1, fontWeight: 600 }}
              >{submitting ? 'Posting…' : 'Post Comment'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExpensePublicView() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('current');
  const [openGroup, setOpenGroup] = useState('curr-office');

  useEffect(() => {
    axios.get(`/api/expenses/public/${token}/`)
      .then(r => setReport(r.data))
      .catch(() => setError('Report not found or link has expired.'));
  }, [token]);

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 15 }}>{error}</div>
    </div>
  );
  if (!report) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ color: '#94a3b8', fontSize: 15 }}>Loading…</div>
    </div>
  );

  const monthName = MONTHS[(report.month || 1) - 1];
  const curr = computeGroups(report.line_items);
  const td = report.tally_data || {};

  const prev = report.prev_month || null;
  const prevTd = prev ? (prev.tally_data || {}) : null;
  const prevMonthName = prev ? MONTHS[(prev.month || 1) - 1] : null;
  const prevG = prev ? computeGroups(prev.line_items) : null;

  const prevActualTotal = prev ? (prev.line_items || []).reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0) : 0;
  const prevAmtReceived = prevTd ? (parseFloat(prevTd.amount_received) || 0) : 0;
  const prevBaseline = prevAmtReceived || (prevG ? prevG.grandTotal : 0);
  // Extra spent = items where no estimate was set (approx=0/null) but actual cost was incurred
  const prevExtraItems = prev
    ? (prev.line_items || []).filter(it =>
        (!it.approximate_amount || parseFloat(it.approximate_amount) === 0) &&
        it.actual_amount && parseFloat(it.actual_amount) > 0)
    : [];
  const prevCarryForward = prevExtraItems.reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0);
  const currToReceive = curr.officeT + curr.internalT;
  const netToRequest = currToReceive + prevCarryForward;
  const currActualTotal = (report.line_items || []).reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0);
  const currAmtReceived = parseFloat(td.amount_received) || 0;
  const currExtraSpent = currActualTotal - currAmtReceived;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '0 0 48px' }}>

        {/* Header */}
        <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '22px 28px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: 4 }}>
              4Sight Operations
            </div>
            <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
              {monthName} {report.year}
            </h1>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {[report.name, report.department, report.branch].filter(Boolean).join(' · ')}
              {(report.start_date || report.end_date) && (
                <span style={{ marginLeft: 12, color: '#94a3b8' }}>
                  {fmtDate(report.start_date)} – {fmtDate(report.end_date)}
                </span>
              )}
            </div>
          </div>
          <a
            href={`/api/expenses/public/${token}/excel/`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
              background: '#0f172a', color: '#fff', padding: '9px 16px',
              borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600,
            }}
          >
            <Download size={13} /> Excel
          </a>
        </div>

        {/* Tab switcher */}
        <div style={{ padding: '16px 28px 0', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 10, padding: 4, border: '1px solid #e2e8f0' }}>
            {[
              { key: 'prev', label: prev ? `${prevMonthName} ${prev.year}` : 'Previous Month', sub: 'Reconciliation' },
              { key: 'current', label: `${monthName} ${report.year}`, sub: 'Current Estimates' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '8px 18px', border: 'none', cursor: 'pointer', borderRadius: 7,
                  background: activeTab === tab.key ? '#fff' : 'transparent',
                  boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600, color: activeTab === tab.key ? '#0f172a' : '#64748b' }}>{tab.label}</span>
                <span style={{ fontSize: 11, color: activeTab === tab.key ? '#10b981' : '#94a3b8' }}>{tab.sub}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Previous month tab — flat table */}
          {activeTab === 'prev' && (
            <>
              {!prev ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                  No previous month report found.
                </div>
              ) : (() => {
                const prevItems = (prev.line_items || []).filter(it =>
                  it.description && (
                    (it.approximate_amount && parseFloat(it.approximate_amount) > 0) ||
                    (it.actual_amount && parseFloat(it.actual_amount) > 0)
                  )
                ).map(it => {
                  const approx = parseFloat(it.approximate_amount) || 0;
                  const actual = parseFloat(it.actual_amount) || 0;
                  const isUnplanned = approx === 0 && actual > 0;
                  return {
                    ...it,
                    approx,
                    actual,
                    displayAmt: actual > 0 ? actual : approx,
                    isActual: actual > 0,
                    isUnplanned,
                    isVendor: it.payment_type === 'vendor',
                  };
                });
                const prevHasUnplanned = prevItems.some(it => it.isUnplanned);
                const prevHasActuals = prevItems.some(it => it.isActual);
                const prevDisplayTotal = prevHasActuals ? prevActualTotal : prevG.grandTotal;

                return (
                  <>
                    {/* Flat table */}
                    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '2px solid #10b981' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                          Expense Details — {prevMonthName} {prev.year}
                        </span>
                        <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.6px' }}>
                          {prevHasActuals ? 'Actuals' : 'Estimated'}
                        </span>
                      </div>

                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f0fdf4' }}>
                            <th style={{ padding: '9px 10px 9px 18px', textAlign: 'center', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '5%' }}>#</th>
                            <th style={{ padding: '9px 18px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '30%' }}>Expense</th>
                            <th style={{ padding: '9px 18px', textAlign: 'right', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '13%' }}>Approx</th>
                            <th style={{ padding: '9px 18px', textAlign: 'right', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '13%' }}>Actual</th>
                            <th style={{ padding: '9px 18px', textAlign: 'center', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '20%' }}>Type</th>
                            <th style={{ padding: '9px 18px', textAlign: 'right', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '24%' }}>Paid To</th>
                          </tr>
                        </thead>
                        <tbody>
                          {prevItems.map((it, i) => {
                            const typ = getItemType(it);
                            return (
                              <tr key={it.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '10px 10px 10px 18px', textAlign: 'center', fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                                  {toRoman(i + 1)}
                                </td>
                                <td style={{ padding: '10px 18px', fontSize: 13.5, color: it.isUnplanned ? '#b45309' : '#111827' }}>
                                  {it.description}{it.isUnplanned ? ' *' : ''}
                                </td>
                                <td style={{ padding: '10px 18px', textAlign: 'right', fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: '#64748b', whiteSpace: 'nowrap' }}>
                                  {it.approx > 0 ? it.approx.toLocaleString('en-IN') : <span style={{ color: '#cbd5e1' }}>—</span>}
                                </td>
                                <td style={{ padding: '10px 18px', textAlign: 'right', fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: it.isUnplanned ? '#b45309' : (it.actual > 0 && it.actual !== it.approx ? '#1d4ed8' : '#111827'), whiteSpace: 'nowrap' }}>
                                  {it.actual > 0 ? it.actual.toLocaleString('en-IN') : <span style={{ color: '#cbd5e1' }}>—</span>}
                                </td>
                                <td style={{ padding: '10px 18px', textAlign: 'center' }}>
                                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99, display: 'inline-block', whiteSpace: 'nowrap', background: typ.bg, color: typ.color }}>
                                    {typ.label}
                                  </span>
                                </td>
                                <td style={{ padding: '10px 18px', textAlign: 'right' }}>
                                  <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 99, display: 'inline-block', whiteSpace: 'nowrap', background: it.isVendor ? '#fef3c7' : '#eff6ff', color: it.isVendor ? '#92400e' : '#1d4ed8' }}>
                                    {it.isVendor ? 'Vendor' : 'Ram'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#f0fdf4', borderTop: '2px solid #10b981' }}>
                            <td></td>
                            <td style={{ padding: '12px 18px', fontSize: 14, fontWeight: 800, color: '#059669' }}>Total</td>
                            <td style={{ padding: '12px 18px', textAlign: 'right', fontSize: 14, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: '#64748b', whiteSpace: 'nowrap' }}>
                              {prevG.grandTotal.toLocaleString('en-IN')}
                            </td>
                            <td style={{ padding: '12px 18px', textAlign: 'right', fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: '#059669', whiteSpace: 'nowrap' }}>
                              {prevActualTotal > 0 ? prevActualTotal.toLocaleString('en-IN') : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            <td></td><td></td>
                          </tr>
                        </tfoot>
                      </table>

                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 18px', borderTop: '1px solid #f1f5f9', background: '#fafafa' }}>
                        {prevHasUnplanned
                          ? <span style={{ fontSize: 11, color: '#b45309' }}>* Unplanned expense — no prior estimate</span>
                          : <span />}
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>4Sight Operations</span>
                      </div>
                    </div>

                    {/* Reconciliation */}
                    {(prevAmtReceived > 0 || prevActualTotal > 0) && (
                      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <SectionLabel>Reconciliation</SectionLabel>
                        <RecRow label="Estimated Total" value={prevG.grandTotal} bold color="#475569" bg="#f8fafc" />
                        {prevTd.rent_transferred && <RecRow label="Rent Transferred (Direct)" value={prevTd.rent_transferred} />}
                        {prevAmtReceived > 0 && <RecRow label="Amount Received" value={prevAmtReceived} bold color="#16a34a" bg="#f0fdf4" />}
                        {prevActualTotal > 0 && <RecRow label="Actual Expense" value={prevActualTotal} bold color="#1d4ed8" bg="#eff6ff" />}
                        {prevActualTotal > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 20px', background: prevCarryForward > 0 ? '#fff1f2' : '#f0fdf4', borderTop: `2px solid ${prevCarryForward > 0 ? '#f43f5e' : '#10b981'}` }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: prevCarryForward > 0 ? '#9f1239' : '#065f46' }}>
                              {prevCarryForward > 0 ? `Extra Spent → carried to ${monthName}` : `Surplus → carried to ${monthName}`}
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 700, color: prevCarryForward > 0 ? '#9f1239' : '#065f46', fontVariantNumeric: 'tabular-nums' }}>
                              {prevCarryForward > 0 ? '+' : '−'} ₹{Math.abs(prevCarryForward).toLocaleString('en-IN')}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {/* Current month tab — flat table */}
          {activeTab === 'current' && (() => {
            // Build flat item list: all items with a description and any amount
            const flatItems = (report.line_items || []).filter(it =>
              it.description && (
                (it.approximate_amount && parseFloat(it.approximate_amount) > 0) ||
                (it.actual_amount && parseFloat(it.actual_amount) > 0)
              )
            ).map(it => {
              const approx = parseFloat(it.approximate_amount) || 0;
              const actual = parseFloat(it.actual_amount) || 0;
              const isUnplanned = approx === 0 && actual > 0;
              return {
                ...it,
                displayAmt: isUnplanned ? actual : approx,
                isUnplanned,
                isVendor: it.payment_type === 'vendor',
              };
            });
            const hasUnplanned = flatItems.some(it => it.isUnplanned);
            const grandTotal = netToRequest + curr.vendorT;
            const officeFormula = flatItems
              .map((it, i) => ({ it, i }))
              .filter(({ it }) => !it.isVendor)
              .map(({ i }) => toRoman(i + 1))
              .join(' + ');

            return (
              <>
                {/* Summary stat strip */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 12 }}>
                  {[
                    { label: 'Total Expected', value: grandTotal, color: '#059669' },
                    { label: 'To Transfer (DP)', value: netToRequest, color: '#1d4ed8' },
                    { label: 'Vendor Direct', value: curr.vendorT, color: '#92400e' },
                  ].map((stat, i) => (
                    <div key={i} style={{ padding: '16px 12px', borderLeft: i > 0 ? '1px solid #e2e8f0' : 'none', textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: stat.color }}>
                        ₹{stat.value.toLocaleString('en-IN')}
                      </div>
                      <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.6px', color: '#6b7280', marginTop: 4, fontWeight: 600 }}>
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Flat expense table */}
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  {/* Top divider with brand context */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '2px solid #10b981' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', letterSpacing: '-.2px' }}>
                      Expense Details — {monthName} {report.year}
                    </span>
                    <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.6px' }}>Estimated</span>
                  </div>

                  {(() => {
                    const officeIndices = flatItems
                      .map((it, i) => ({ it, i }))
                      .filter(({ it }) => !it.isVendor)
                      .map(({ i }) => toRoman(i + 1));
                    const officeFormula = officeIndices.join(' + ');
                    return (
                      <>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f0fdf4' }}>
                              <th style={{ padding: '9px 10px 9px 18px', textAlign: 'center', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '5%' }}>#</th>
                              <th style={{ padding: '9px 18px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '35%' }}>Expense</th>
                              <th style={{ padding: '9px 18px', textAlign: 'right', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '16%' }}>INR</th>
                              <th style={{ padding: '9px 18px', textAlign: 'center', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '20%' }}>Type</th>
                              <th style={{ padding: '9px 18px', textAlign: 'right', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.7px', color: '#059669', borderBottom: '1px solid #d1fae5', width: '24%' }}>Paid To</th>
                            </tr>
                          </thead>
                          <tbody>
                            {flatItems.map((it, i) => {
                              const typ = getItemType(it);
                              return (
                                <tr key={it.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '10px 10px 10px 18px', textAlign: 'center', fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                                    {toRoman(i + 1)}
                                  </td>
                                  <td style={{ padding: '10px 18px', fontSize: 13.5, color: it.isUnplanned ? '#b45309' : '#111827' }}>
                                    {it.description}{it.isUnplanned ? ' *' : ''}
                                  </td>
                                  <td style={{ padding: '10px 18px', textAlign: 'right', fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: it.isUnplanned ? '#b45309' : '#111827', whiteSpace: 'nowrap' }}>
                                    {it.displayAmt.toLocaleString('en-IN')}
                                  </td>
                                  <td style={{ padding: '10px 18px', textAlign: 'center' }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99, display: 'inline-block', whiteSpace: 'nowrap', background: typ.bg, color: typ.color }}>
                                      {typ.label}
                                    </span>
                                  </td>
                                  <td style={{ padding: '10px 18px', textAlign: 'right' }}>
                                    <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 99, display: 'inline-block', whiteSpace: 'nowrap', background: it.isVendor ? '#fef3c7' : '#eff6ff', color: it.isVendor ? '#92400e' : '#1d4ed8' }}>
                                      {it.isVendor ? 'Vendor' : 'Ram'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: '#f0fdf4', borderTop: '2px solid #10b981' }}>
                              <td></td>
                              <td style={{ padding: '12px 18px', fontSize: 14, fontWeight: 800, color: '#059669' }}>Total</td>
                              <td style={{ padding: '12px 18px', textAlign: 'right', fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: '#059669', whiteSpace: 'nowrap' }}>
                                {grandTotal.toLocaleString('en-IN')}
                              </td>
                              <td></td><td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </>
                    );
                  })()}

                  {/* Footnote */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 18px', borderTop: '1px solid #f1f5f9', background: '#fafafa' }}>
                    {hasUnplanned
                      ? <span style={{ fontSize: 11, color: '#b45309' }}>* Unplanned expense — no prior estimate</span>
                      : <span />}
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>4Sight Operations</span>
                  </div>
                </div>

                {/* Transfer summary */}
                {prev && (
                  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <SectionLabel>Amount to be Transferred This Month</SectionLabel>
                    <RecRow label={`This Month's Office Expense${officeFormula ? ` (${officeFormula})` : ''}`} value={currToReceive} bold color="#475569" bg="#f8fafc" />
                    {prevCarryForward !== 0 && (
                      <>
                        <div style={{ padding: '8px 20px 4px', background: '#fafafa', borderTop: '1px solid #f1f5f9' }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {prevMonthName} carry-forward detail
                          </span>
                        </div>
                        {prevAmtReceived > 0 && (
                          <>
                            {prevActualTotal > 0 && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 20px 6px 32px', background: '#fafafa' }}>
                                <span style={{ fontSize: 13, color: '#64748b' }}>{prevMonthName} Actual Expense</span>
                                <span style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>₹{prevActualTotal.toLocaleString('en-IN')}</span>
                              </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 20px 6px 32px', background: '#fafafa' }}>
                              <span style={{ fontSize: 13, color: '#64748b' }}>{prevMonthName} Amount Received</span>
                              <span style={{ fontSize: 13, color: '#16a34a', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>₹{prevAmtReceived.toLocaleString('en-IN')}</span>
                            </div>
                          </>
                        )}
                        {prevExtraItems.length > 0 && prevExtraItems.map((it, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 20px 5px 36px', background: '#fef2f2', borderTop: i === 0 ? '1px dashed #e2e8f0' : 'none' }}>
                            <span style={{ fontSize: 12, color: '#9f1239' }}>{it.description}</span>
                            <span style={{ fontSize: 12, color: '#9f1239', fontVariantNumeric: 'tabular-nums' }}>₹{parseFloat(it.actual_amount).toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px', background: prevCarryForward > 0 ? '#fff1f2' : '#f0fdf4', borderTop: prevExtraItems.length > 0 ? '1px solid #fecaca' : '1px dashed #e2e8f0' }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: prevCarryForward > 0 ? '#9f1239' : '#065f46' }}>
                            {prevMonthName} {prevCarryForward > 0 ? 'Extra Spent → added to this bill' : 'Surplus → deducted from this bill'}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: prevCarryForward > 0 ? '#9f1239' : '#065f46', fontVariantNumeric: 'tabular-nums' }}>
                            {prevCarryForward > 0 ? '+' : '−'} ₹{Math.abs(prevCarryForward).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 20px', background: '#fff1f2', borderTop: '2px solid #f43f5e' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#9f1239' }}>Net Amount to be Transferred (DP Head)</span>
                      <span style={{ fontSize: 16, fontWeight: 800, color: '#9f1239', fontVariantNumeric: 'tabular-nums' }}>₹{netToRequest.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                )}

                {/* Reconciliation (only when actuals exist) */}
                {(currAmtReceived > 0 || currActualTotal > 0 || td.rent_transferred) && (
                  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <SectionLabel>Reconciliation</SectionLabel>
                    {td.rent_transferred && <RecRow label="Rent Transferred (Direct)" value={td.rent_transferred} />}
                    {currAmtReceived > 0 && <RecRow label="Amount Received" value={currAmtReceived} bold color="#16a34a" bg="#f0fdf4" />}
                    {currActualTotal > 0 && <RecRow label="Actual Expense" value={currActualTotal} bold color="#1d4ed8" bg="#eff6ff" />}
                    {currActualTotal > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 20px', background: currExtraSpent > 0 ? '#fff1f2' : '#f0fdf4', borderTop: `2px solid ${currExtraSpent > 0 ? '#f43f5e' : '#10b981'}` }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: currExtraSpent > 0 ? '#9f1239' : '#065f46' }}>
                          {currExtraSpent > 0 ? 'Extra Spent (carries to next month)' : 'Surplus (carries to next month)'}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: currExtraSpent > 0 ? '#9f1239' : '#065f46', fontVariantNumeric: 'tabular-nums' }}>
                          {currExtraSpent > 0 ? '+' : '−'} ₹{Math.abs(currExtraSpent).toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Invoices */}
                {report.invoices && report.invoices.length > 0 && (
                  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <SectionLabel>Attached Invoices ({report.invoices.length})</SectionLabel>
                    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {report.invoices.map(inv => (
                        <div key={inv.id} style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                          background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.8" style={{ flexShrink: 0 }}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                          </svg>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {inv.original_filename}
                            </div>
                            {inv.description && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{inv.description}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <a href={inv.file} target="_blank" rel="noopener noreferrer" style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                              borderRadius: 5, border: '1px solid #e2e8f0', background: '#fff',
                              textDecoration: 'none', color: '#3b82f6', fontSize: 12, fontWeight: 600,
                            }}>View</a>
                            <a href={inv.file} download={inv.original_filename} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                              borderRadius: 5, border: 'none', background: '#10b981',
                              textDecoration: 'none', color: '#fff', fontSize: 12, fontWeight: 600,
                            }}>Download</a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          <CommentsSection token={token} />

          <div style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 11, paddingTop: 8 }}>
            4Sight Operations · Read-only shared view
          </div>
        </div>
      </div>
    </div>
  );
}
